import { get, pick } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { Person } from 'sr-types/lib/person/v1/graphql';
import { Production } from 'sr-types/lib/production/v1/graphql';
import { DateRange, Reference, Location } from 'sr-types/lib/resource/v1/graphql';
import { Contact, ContactInfo } from 'sr-types/lib/search/v1/graphql';
import {
    AttachmentInput,
    InquiryInput,
    InquiryMessage,
    InquiryPreviewInput,
    NoteInput,
    SupplierDetails
} from 'sr-types/lib/shortlist/v1/graphql';
import Validator from '../../../common/form/Validator';
import { ValidationRulesType } from '../../../production/helpers/productionUtils';
import { getDateRangeValidators } from '../../../supply/booking/ScheduleCalculator';
import { ReferenceWithLineItemId } from '../../shortlist';
import { useGetOrganizationDetailsForInquiry, useGetPersonDetailsForInquiry } from '../../shortlistApi';
import { Filter } from '../../../common/components/types';
import { VENDOR_KEY } from '../../../supply/opportunity/common/opportunityConstants';

export const PEOPLE_TYPE = 'people';
export const ORGANIZATION_TYPE = 'organizations';

export type PeopleType = ReferenceWithLineItemId & {
    contact: Contact & { id?: string };
    contactInfo?: ContactInfo;
};

export type OrganizationType = ReferenceWithLineItemId & {
    contact: Contact & { id?: string };
    contactInfo?: ContactInfo;
};

export type CommonInviteFormData = {
    resource: Reference;
    resourceCategory: string;
    production: Production;
    productionSummary: string;
    dateRange: DateRange;
    locations: Location[];
    personalizedMessage?: string;
    productionOptions?: { id: string; label: string; key?: string }[];
    attachments?: AttachmentInput[];
    senderOrganizationName?: string;
};

export type PersonInviteFormData = CommonInviteFormData & {
    people: PeopleType[];
    role: string;
    productionSummary?: string;
};

export type OrganizationInviteFormData = CommonInviteFormData & {
    organizations: OrganizationType[];
    resourceNote: NoteInput & { text: string };
    activities: Reference[];
    notifyContacts?: Contact[];
    senderContacts?: Contact[];
};

export type InquiryPropsType = {
    type: string;
    entityDetails?: any;
    isEntityLoading?: boolean;
    toInquiryInput?: (state) => InquiryInput[];
    validationRules: ValidationRulesType;
    isModalOpen?: boolean;
    onCloseModal?: () => void;
    data: PersonInviteFormData | OrganizationInviteFormData;
    onSendInquirySuccess?: (editState: InquiryInput[]) => void;
    productionData?: Production;
    personData?: Person;
    isLoading?: boolean;
    isRequestAvailsForUser?: boolean;
    isInStepper?: boolean;
    filters?: Filter;
    expandMarketplace?: () => void;
    expandedContainer?: boolean;
    showCandidateListOnly?: boolean;
};

export type PersonInviteFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
    isEmailModalOpen: boolean;
    isPreview: boolean;
    setIsPreview: Dispatch<SetStateAction<boolean>>;
    sendInvitation: () => void;
    productionName: string;
    productionDateRange: DateRange;
    productionOptions: { id: string; label: string; key?: string }[];
    peopleWithNoContact: any[];
    onClickContinue: () => void;
    isSaving: boolean;
    isLoading: boolean;
    entityDetails: any;
    getInquiryPreviewData: (options: any) => void;
    isPreviewLoading: boolean;
    previewData: InquiryMessage;
    saveAttachmentTags: (inputs: AttachmentInput[]) => void;
    onDeleteAttachmentFromState: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    setIsInUploadMode: Dispatch<SetStateAction<boolean>>;
    isAttachmentsInUploadMode: boolean;
};

export type PersonInviteFormProps = {
    productionName: string;
    productionDateRange: DateRange;
    productionOptions: { id: string; label: string; key?: string }[];
    saveAttachmentTags: (inputs: AttachmentInput[]) => void;
    onDeleteAttachmentFromState: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    setIsInUploadMode: Dispatch<SetStateAction<boolean>>;
    isAttachmentsInUploadMode: boolean;
    isRequestAvailsForUser?: boolean;
    filters?: Filter;
    expandMarketplace?: () => void;
    expandedContainer?: boolean;
    showCandidateListOnly?: boolean;
};

export type OrganizationInviteFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
    isSaving: boolean;
    isEmailModalOpen: boolean;
    itemsWithNoEmail: any[];
    data: OrganizationInviteFormData;
    onClickContinue: () => void;
    isPreview: boolean;
    setIsPreview: Dispatch<SetStateAction<boolean>>;
    sendInvitation: () => void;
    entityDetails: any;
    getInquiryPreviewData: (options: any) => void;
    isPreviewLoading: boolean;
    previewData: InquiryMessage;
    saveAttachmentTags: (inputs: AttachmentInput[]) => void;
    onDeleteAttachmentFromState: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    setIsInUploadMode: Dispatch<SetStateAction<boolean>>;
    isAttachmentsInUploadMode: boolean;
};

export type OrganizationInviteFormProps = {
    data: OrganizationInviteFormData;
    expandMarketplace?: () => void;
    expandedContainer?: boolean;
    isRequestAvailsForUser?: boolean;
    filters?: Filter;
    saveAttachmentTags: (inputs: AttachmentInput[]) => void;
    onDeleteAttachmentFromState: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    setIsInUploadMode: Dispatch<SetStateAction<boolean>>;
    isAttachmentsInUploadMode: boolean;
};

export const personValidationRules = (showCandidateListOnly = false): ValidationRulesType => {
    if (showCandidateListOnly) {
        return {person: [Validator.RULES.isRequired]};
    } else {
        return {
            ...getDateRangeValidators('dateRange'),
            people: [Validator.RULES.isRequired],
            production: [Validator.RULES.isRequired],
            role: [Validator.RULES.isRequired],
            personalizedMessage: [Validator.RULES.isRequired]
        };
    }
};

export const organizationValidationRules: ValidationRulesType = {
    ...getDateRangeValidators('dateRange'),
    organizations: [Validator.RULES.isRequired],
    production: [Validator.RULES.isRequired]
};

export const alternateProductionNameValidations = {
    alternateName: [Validator.RULES.isRequired],
    alternateLabel: [Validator.RULES.isRequired]
};

const commonInquiryInputObj = (editState: any, inquiryFor: string) => {
    const allAttachmentReference = editState.attachments?.map((attachment: AttachmentInput) => {
        return { ...attachment.entityReference };
    });
    return {
        assignmentPeriod: {
            ...editState.dateRange
        },
        locations: editState.locations,
        message: editState.personalizedMessage,
        resources: [
            {
                reference: editState.resource,
                name: editState.resource.label,
                category: editState.resourceCategory?.value || editState.resourceCategory,
                isPrimary: true
            }
        ],
        InquiryFor: inquiryFor,
        Attachments: allAttachmentReference,
        SenderOrganizationName: editState.senderOrganizationName,
        senderContacts: editState.senderContacts?.map((contact: Contact) => {
            return {
                contactInfo: contact.contactInfo,
            } as Contact;
        })
    };
};

export const orgInquiryPayload = (editState: OrganizationInviteFormData, org: OrganizationType, activities = []) => {
    const commonInquiryInputItems = commonInquiryInputObj(editState, VENDOR_KEY);
    const orgContact = [] as Contact[];
    if (org.contact) {
        orgContact.push({...org.contact, isPrimary: true});
    } else if (org.contactInfo) {
        orgContact.push({
            contactInfo: org.contactInfo,
            isPrimary: true
        } as Contact);
    }
    if (editState.notifyContacts?.length) {
        editState.notifyContacts.forEach((contact) => {
            orgContact.push({
                contactInfo: contact.contactInfo,
            } as Contact);
        })
    }

    return {
        ...commonInquiryInputItems,
        receiverContacts: orgContact,
        production: {
            name: editState.production.name,
            reference: editState.production.reference,
            summary: editState.productionSummary,
            activities,
            productionType: editState.production.productionType
        },
        resourceNote: {
            content: editState.resourceNote?.text || '',
            reference: editState.resourceNote?.entityReference
        }
    };
};

export const toOrgInquiryInput = (editState: OrganizationInviteFormData): InquiryInput[] => {
    const activities = editState?.activities.length
        ? editState.activities.map((activity) => {
              return {
                  name: activity.label,
                  reference: activity
              };
          })
        : [];
    const inquiryInput: InquiryInput[] = editState.organizations.map((organization) => {
        const updatedInquiryInput = {
            shortlistLineItemId: organization.shortlistLineItemId,
            inquiryPayload: {
                ...orgInquiryPayload(editState, organization, activities)
            }
        };

        return updatedInquiryInput;
    }) as InquiryInput[];

    return inquiryInput;
};

export const personInquiryPayload = (editState: PersonInviteFormData, person: PeopleType) => {
    const commonInquiryInputItems = commonInquiryInputObj(editState, 'individual');
    const personContact = person.contact ? [{...person.contact, isPrimary: true}] : []
    return {
        ...commonInquiryInputItems,
        receiverContacts: personContact,
        production: {
            name: editState.production.name,
            reference: editState.production.reference,
            summary: editState.productionSummary,
            productionType: editState.production.productionType
        },
        role: editState.role
    };
};

export const toPersonInquiryInput = (editState: PersonInviteFormData): InquiryInput[] => {
    const inquiryInput: InquiryInput[] = editState.people?.map((person) => {
        return {
            shortlistLineItemId: person.shortlistLineItemId,
            inquiryPayload: {
                ...personInquiryPayload(editState, person)
            }
        };
    }) as InquiryInput[];

    return inquiryInput;
};

export const checkForEmail = (
    data: any,
    nameInState: string,
    state: any,
    setValidations: React.Dispatch<React.SetStateAction<{}>>,
    setIsEmailModalOpen: React.Dispatch<React.SetStateAction<{}>>,
    setItemsWithNoEmail: React.Dispatch<React.SetStateAction<{}>>
) => {
    const selectedItems = get(state, nameInState);
    const validations = {};

    selectedItems?.forEach((selectedItem, index) => {
        const itemDetails = data.find((item) => selectedItem.id === item.id);
        const isEmailUnavailableInCrew =
            itemDetails?.reference?.type === 'Person' && !itemDetails?.contactInfo?.email?.address;

        const isEmailUnavailableInOrganization =
            itemDetails?.reference?.type !== 'Person' && !itemDetails?.contact?.contactInfo?.email?.address;

        if (isEmailUnavailableInCrew || isEmailUnavailableInOrganization) {
            setEmptyContact(selectedItem, itemDetails, setItemsWithNoEmail);
            setContactValidation(index, nameInState, validations, setValidations);
            setIsEmailModalOpen(true);
        } else {
            itemDetails.contact = pick(
                itemDetails.contact,
                'name',
                'isPrimary',
                'contactInfo',
                'role',
                'personReference'
            );
            setIsEmailModalOpen(false);
        }
    });
};

const setEmptyContact = (
    selectedItem: any,
    itemDetails: any,
    setItemsWithNoEmail: React.Dispatch<React.SetStateAction<{}>>
) => {
    const itemDetailsWithNoContact = [];

    selectedItem.contact = {
        contactInfo: {
            email: {
                address: '',
                typeString: 'Business',
                verified: false
            }
        }
    };

    itemDetailsWithNoContact.push(itemDetails);
    setItemsWithNoEmail((prevState: any[]) => [...prevState, ...itemDetailsWithNoContact]);
};

const setContactValidation = (
    index: number,
    name: string,
    validations: any,
    setValidations: React.Dispatch<React.SetStateAction<{}>>
) => {
    validations[`${name}[${index}].contact.contactInfo.email.address`] = [
        Validator.RULES.isRequired,
        Validator.RULES.email
    ];
    setValidations(validations);
};

export const getInquiryPersonalisedMessageKey = (isCrew: boolean) => {
    return isCrew ? 'shortlist.lineItem.person.message' : 'shortlist.lineItem.inquiry.message';
};

export const useInquiryInviteeData = (isCrew: boolean, filter: any[]) => {
    const { personData, personLoading } = useGetPersonDetailsForInquiry(filter, !isCrew);
    const { organizationData, organizationLoading } = useGetOrganizationDetailsForInquiry(filter, isCrew);

    if (isCrew) {
        return { inviteeSearchData: personData, isSearchLoading: personLoading };
    } else {
        return { inviteeSearchData: organizationData, isSearchLoading: organizationLoading };
    }
};

export const getInquiryPreviewInput = (type: string, state, itemDetails) => {
    const supplierDetails: SupplierDetails = {
        contact: {
            contactInfo: itemDetails?.contactInfo || itemDetails?.contact?.contactInfo
        }
    };
    let inquiryInput: InquiryInput[];
    if (type == PEOPLE_TYPE) {
        inquiryInput = toPersonInquiryInput(state);
        const names = itemDetails?.name ? itemDetails.name.split(' ') : [];
        if (names.length) {
            supplierDetails.personName = {
                firstName: names[0],
                lastName: names.length > 1 ? names.slice(1).join(' ') : ''
            };
        }
    } else if (type == ORGANIZATION_TYPE) {
        inquiryInput = toOrgInquiryInput(state);
        supplierDetails.orgPublicName = itemDetails?.name;
    }

    if (inquiryInput?.length) {
        const inquiryPreviewInput: InquiryPreviewInput = {
            shortlistLineItemId: inquiryInput[0].shortlistLineItemId || '',
            supplierDetails,
            inquiryDetails: inquiryInput[0].inquiryPayload
        };
        return inquiryPreviewInput;
    }
    return null;
};
